import { GetInputProps, Reset, SetFieldValue } from "@mantine/form/lib/types"
import {
  CodenameTag,
  FormInput,
  FormQuerySelector,
  FormSelector,
  Modal,
  TextArea,
} from "@vesatogo/grass-core"
import { FormDateInput } from "@vesatogo/grass-dates"
import { FileUpload } from "@vesatogo/grass-dropzone"
import classNames from "classnames"
import dayjs from "dayjs"
import { useSearchParams } from "react-router-dom"
import { OperationContext } from "urql"
import { CustomToast } from "~/components/CustomToast"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import {
  useAddUpdateSharedEntityMutation,
  useAllPaymentMethodsQuery,
  useAuthenticatedUserQuery,
  useDepartmentPaymentListQuery,
  useInsertPlantSettlementMutation,
  useUsersQuery,
} from "~/generated/graphql"

export const PlantTransaction = ({
  state,
  resetForm,
  getInputProps,
  getFileUploadProps,
  setFieldValue,
  refetch,
  kind = "credit",
}: {
  state?: any
  resetForm: Reset
  getInputProps: GetInputProps<Record<string, unknown>>
  refetch?: (opts?: Partial<OperationContext> | undefined) => void
  getFileUploadProps?: any
  setFieldValue?: SetFieldValue<any>
  kind?: "credit" | "debit"
}) => {
  const [params, setParams] = useSearchParams()
  const isOpen = Boolean(params.get("addPlantTransaction"))

  // const form = useForm()
  // const {reset} = form
  const [, insertPlantTransaction] = useInsertPlantSettlementMutation()
  const [, addUpdateSharedEntity] = useAddUpdateSharedEntityMutation()
  const onClose = () => {
    params.delete("addPlantTransaction")
    setParams(params)
    resetForm?.()
  }

  const [{ data }] = useAuthenticatedUserQuery()

  const departments = data?.me?.agent?.departments?.map(
    item => item?.department
  )

  const [{ data: bankData }] = useDepartmentPaymentListQuery({
    variables: {
      where: {
        id: {
          _eq: state?.plant?.id,
        },
      },
    },
  })

  const onSubmit = async () => {
    if (
      !state?.date ||
      !state?.plant ||
      !state?.customer ||
      !state?.amount ||
      !state?.transaction_id ||
      !state?.bank_name
    ) {
      return CustomToast({
        intent: "danger",
        title: "Please enter all mandatory details!",
      })
    }
    const { data: userData, error: userError } = await addUpdateSharedEntity({
      reference_id: state?.customer?.codename,
      group_name: "kite.base_user",
      name: state?.customer?.name,
    })
    if (userError)
      return CustomToast({
        intent: "danger",
        title:
          "Something went wrong while fetching Customer details! " +
          userError?.message,
      })
    const userId = userData?.insert_setu_trade_shared_entity_one?.id

    const { data: deptData, error: deptError } = await addUpdateSharedEntity({
      reference_id: state?.plant?.codename,
      group_name: SharedEntityGroup.KiteDepartment,
      name: state?.plant?.name,
    })
    if (deptError)
      return CustomToast({
        intent: "danger",
        title:
          "Something went wrong while fetching Department details! " +
          deptError?.message,
      })
    const deptId = deptData?.insert_setu_trade_shared_entity_one?.id

    const { data: bankData, error: bankError } = await addUpdateSharedEntity({
      reference_id: state?.bank_name?.payment_detail?.id?.toString(),
      group_name: "kite.payment_detail",
      name: state?.bank_name?.payment_detail?.account_holder_name,
    })
    if (bankError)
      return CustomToast({
        intent: "danger",
        title:
          "Something went wrong while fetching Bank details! " +
          bankError?.message,
      })
    const bankId = bankData?.insert_setu_trade_shared_entity_one?.id

    const { error } = await insertPlantTransaction({
      object: {
        date: dayjs(state?.date).format("MM-DD-YYYY") || new Date(),
        department_id: deptId,
        user_id: userId,
        kind,
        is_active: true,
        transactions: {
          data: [
            {
              amount: state?.amount,
              date: dayjs(state?.date).format("MM-DD-YYYY") || new Date(),
              mode_id: state?.payment_mode?.id,
              transaction_id: state?.transaction_id?.toString(),
              bank_id: bankId,
              remark: state?.narration,
              account_no: state?.bank_name?.payment_detail?.account_number,
              attachments: state?.attachments?.length
                ? {
                    data: state?.attachments?.map(att => {
                      return { media_id: att.id }
                    }),
                  }
                : undefined,
            },
          ],
        },
      },
    })

    refetch?.({ requestPolicy: "network-only" })
    resetForm?.()
    onClose()
    if (error) {
      return CustomToast({
        intent: "danger",
        title: "Something went wrong! " + error.message,
      })
    }
    return CustomToast({
      title: "Successfully added Plant Transaction!",
    })
  }

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      title="Plant Transaction Details"
      bodyClassName="overflow-visible max-w-5xl"
      primaryActionButtonProps={{
        text: "Yes, Confirm",
        onClick: onSubmit,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        onClick: onClose,
      }}
    >
      <div className="grid grid-cols-4 gap-3">
        <div className={classNames("row-span-4")}>
          <FileUpload
            {...getFileUploadProps(`attachments`, 4)}
            endpoint={`https://${
              import.meta.env.VITE_REST_URL
            }/rest/trade/uploader/`}
            appName="setutrade"
            onSuccess={(file: any) => {
              setFieldValue?.("attachments", [
                ...state?.attachments,
                file[0]?.data?.media,
              ])
            }}
          />
        </div>
        <FormDateInput label="Date" required {...getInputProps("date")} />
        <FormSelector
          // dataHook={useAllDepartmentsQuery}
          label="Plant Name"
          required
          options={departments}
          {...getInputProps("plant")}
        />
        <FormQuerySelector
          dataHook={useUsersQuery}
          label="Customer Name"
          required
          serverSideQuery
          getOptionLabel={option => {
            return (
              <>
                <div>{option?.name}</div>
                <CodenameTag
                  referenceId={option?.external_reference_id}
                ></CodenameTag>
              </>
            )
          }}
          variables={{ groups: ["Farmer", "Trader"] }}
          {...getInputProps("customer")}
        />
        <FormInput
          label="Received Amount"
          required
          placeholder="₹"
          {...getInputProps("amount")}
        />
        <FormSelector
          label="Bank Name"
          {...getInputProps(`bank_name`)}
          required
          getOptionLabel={op => {
            return op?.payment_detail?.bank_name
          }}
          options={bankData ? bankData?.department?.[0]?.payment_details : []}
        />
        <FormQuerySelector
          dataHook={useAllPaymentMethodsQuery}
          label="Payment Mode"
          required
          {...getInputProps("payment_mode")}
        />
        <FormInput
          label="Transaction Id"
          required
          {...getInputProps("transaction_id")}
        />
        <div className="col-span-2">
          <TextArea
            className="w-full"
            label="Narration"
            {...getInputProps("narration")}
          />
        </div>
      </div>
    </Modal>
  )
}
